import type { PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';

import type { MultiStepState } from './use-multi-step-form';

/**
 * Sentinel value used to detect missing or nested providers.
 */
const UNINITIALIZED = Symbol('MultiStepFormContext.uninitialized');

const MultiStepFormContext = createContext<
  MultiStepState | typeof UNINITIALIZED
>(UNINITIALIZED);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export interface MultiStepFormProviderProps extends PropsWithChildren {
  /**
   * Step navigation state to expose to descendant consumers.
   */
  value: MultiStepState;
}

/**
 * Provides multi-step form navigation context to descendants.
 *
 * Only one `MultiStepFormProvider` should be active at a time. Nesting a
 * second provider logs a warning to help catch accidental conflicts during
 * development.
 */
export const MultiStepFormProvider = ({
  children,
  value,
}: MultiStepFormProviderProps) => {
  const existing = useContext(MultiStepFormContext);

  if (existing !== UNINITIALIZED) {
    console.warn(
      'MultiStepFormProvider: detected nested provider. Only one multi-step form should be active at a time. The inner provider will shadow the outer one.',
    );
  }

  return (
    <MultiStepFormContext.Provider value={value}>
      {children}
    </MultiStepFormContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Consumer hook
// ---------------------------------------------------------------------------

/**
 * Returns the current multi-step form navigation context.
 *
 * Must be called within a {@link MultiStepFormProvider}.
 */
export const useMultiStepFormContext = (): MultiStepState => {
  const context = useContext(MultiStepFormContext);

  if (context === UNINITIALIZED) {
    throw new Error(
      'useMultiStepFormContext must be used within a <MultiStepFormProvider>.',
    );
  }

  return context;
};
